import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { initializeSocket, disconnectSocket, getSocket } from '../config/socket';
import { getCookie } from '../utils/cookies';

const SocketContext = createContext();

export const useSocket = () => {
	const context = useContext(SocketContext);
	if (!context) {
		throw new Error('useSocket must be used within SocketProvider');
	}
	return context;
};

export const SocketProvider = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const [socket, setSocket] = useState(null);
	const [connected, setConnected] = useState(false);
	const [onlineUsers, setOnlineUsers] = useState(new Set());

	useEffect(() => {
		if (isAuthenticated) {
			const token = getCookie('token');
			if (!token) return;
			const newSocket = initializeSocket(token);
			setSocket(newSocket);

			newSocket.on('connect', () => {
				setConnected(true);
			});

			newSocket.on('disconnect', () => {
				setConnected(false);
			});

			newSocket.on('user:online', ({ userId }) => {
				setOnlineUsers(prev => new Set([...prev, userId]));
			});

			newSocket.on('user:offline', ({ userId }) => {
				setOnlineUsers(prev => {
					const newSet = new Set(prev);
					newSet.delete(userId);
					return newSet;
				});
			});

			return () => {
				disconnectSocket();
				setSocket(null);
				setConnected(false);
			};
		}
	}, [isAuthenticated]);

	const value = {
		socket,
		connected,
		onlineUsers,
		isUserOnline: (userId) => onlineUsers.has(userId)
	};

	return (
		<SocketContext.Provider value={ value }>
			{ children }
		</SocketContext.Provider>
	);
};
